import { useState, useMemo, useRef } from 'react';
import SlDialog from '@shoelace-style/shoelace/dist/react/dialog/index.js';
import SlInput from '@shoelace-style/shoelace/dist/react/input/index.js';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import SlCheckbox from '@shoelace-style/shoelace/dist/react/checkbox/index.js';
import { useTranslation } from 'react-i18next';

interface GroupedPickerItem {
	id: number;
	name: string;
	group: string;
}

interface GroupedPickerDialogProps {
	isOpen: boolean;
	label: string;
	items: GroupedPickerItem[];
	groupLabels: Record<string, string>;
	mode: 'single' | 'multi';
	selectedIDs: number[];
	onConfirm: (ids: number[]) => void;
	onHide: () => void;
	searchPlaceholder?: string;
	maxSelected?: number;
}

const GroupedPickerDialog = ({
	isOpen,
	label,
	items,
	groupLabels,
	mode,
	selectedIDs,
	onConfirm,
	onHide,
	searchPlaceholder,
	maxSelected,
}: GroupedPickerDialogProps) => {
	const [view, setView] = useState<'groups' | 'items'>('groups');
	const [activeGroup, setActiveGroup] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [pendingSelection, setPendingSelection] = useState<Set<number>>(new Set());

	const searchInputRef = useRef<any>(null);

	const { t } = useTranslation();

	const groups = useMemo(() => {
		const map: Record<string, GroupedPickerItem[]> = {};
		for (const item of items) {
			if (!map[item.group]) {
				map[item.group] = [];
			}
			map[item.group].push(item);
		}
		return map;
	}, [items]);

	const groupKeys = useMemo(() => Object.keys(groups), [groups]);

	const activeGroupItems = useMemo(() => {
		if (activeGroup == null) {
			return [];
		}
		return groups[activeGroup] || [];
	}, [groups, activeGroup]);

	const searchResults = useMemo(() => {
		if (searchTerm === '') {
			return [];
		}
		const term = normalize(searchTerm);
		return items.filter((item) => normalize(item.name).includes(term));
	}, [items, searchTerm]);

	const selectedItems = useMemo(() => {
		const itemMap = new Map(items.map((item) => [item.id, item]));
		return Array.from(pendingSelection)
			.map((id) => itemMap.get(id))
			.filter((item) => item != null);
	}, [items, pendingSelection]);

	const isAtMax = maxSelected != null && pendingSelection.size >= maxSelected;

	const onClickGroup = (groupSlug: string) => {
		setActiveGroup(groupSlug);
		setView('items');
	};

	const onClickBack = () => {
		setActiveGroup(null);
		setView('groups');
	};

	const onClickItem = (item: GroupedPickerItem) => {
		if (mode === 'single') {
			onConfirm([item.id]);
		} else {
			if (isAtMax && !pendingSelection.has(item.id)) {
				return;
			}
			setPendingSelection((prev) => {
				const selected = new Set(prev);
				if (selected.has(item.id)) {
					selected.delete(item.id);
				} else {
					selected.add(item.id);
				}
				return selected;
			});
		}
	};

	const onRemoveChip = (id: number) => {
		setPendingSelection((prev) => {
			const next = new Set(prev);
			next.delete(id);
			return next;
		});
	};

	const onClickConfirm = () => {
		onConfirm(Array.from(pendingSelection));
	};

	const onSearchInput = (e: any) => {
		setSearchTerm(e.target.value);
	};

	const onAfterHide = () => {
		setView('groups');
		setActiveGroup(null);
		setSearchTerm('');
		setPendingSelection(new Set());
		onHide();
	};

	const onShow = () => {
		if (mode === 'multi') {
			setPendingSelection(new Set(selectedIDs));
		}
	};

	const isSearching = searchTerm !== '';

	const itemClassName = (item: GroupedPickerItem) => {
		let cls = 'product__grouped-picker-item';
		if (mode === 'multi' && isAtMax && !pendingSelection.has(item.id)) {
			cls += ' product__grouped-picker-item--disabled';
		}
		return cls;
	};

	const renderGroupsGrid = () => (
		<div className='product__grouped-picker-groups'>
			{groupKeys.map((k) => (
				<button
					key={k}
					className='product__grouped-picker-group'
					onClick={() => onClickGroup(k)}
				>
					<div>{groupLabels[k] || k}</div>
					<div className='product__grouped-picker-group-count'>{groups[k].length}</div>
				</button>
			))}
		</div>
	);

	const renderGroupItems = () => (
		<>
			<button className='product__grouped-picker-back' onClick={onClickBack}>
				<SlIcon name='arrow-left' />
				{t('Back')}
			</button>
			<div className='product__grouped-picker-items'>
				{activeGroupItems.map((item) => (
					<button
						key={item.id}
						className={itemClassName(item)}
						onClick={() => onClickItem(item)}
					>
						{mode === 'multi' && (
							<SlCheckbox
								checked={pendingSelection.has(item.id)}
								disabled={isAtMax && !pendingSelection.has(item.id)}
							/>
						)}
						{mode === 'single' && selectedIDs.includes(item.id) && (
							<SlIcon className='product__picker-check' name='check-lg' />
						)}
						{item.name}
					</button>
				))}
			</div>
		</>
	);

	const renderSearchResults = () => (
		<div className='product__grouped-picker-items'>
			{searchResults.length === 0 ? (
				<div className='product__grouped-picker-empty'>{t('No results found')}</div>
			) : (
				searchResults.map((item) => (
					<button
						key={item.id}
						className={itemClassName(item)}
						onClick={() => onClickItem(item)}
					>
						{mode === 'multi' && (
							<SlCheckbox
								checked={pendingSelection.has(item.id)}
								disabled={isAtMax && !pendingSelection.has(item.id)}
							/>
						)}
						{mode === 'single' && selectedIDs.includes(item.id) && (
							<SlIcon className='product__picker-check' name='check-lg' />
						)}
						<span className='product__grouped-picker-item-label'>
							<span className='product__grouped-picker-item-group'>
								{groupLabels[item.group] || item.group}
								{' > '}
							</span>
							{item.name}
						</span>
					</button>
				))
			)}
		</div>
	);

	const countText = maxSelected
		? `${pendingSelection.size} / ${maxSelected}`
		: String(pendingSelection.size);

	return (
		<SlDialog
			className='product__grouped-picker-dialog'
			label={label}
			open={isOpen}
			onSlAfterHide={onAfterHide}
			onSlShow={onShow}
			onSlAfterShow={() => { if (window.innerWidth > 900) searchInputRef.current?.focus(); }}
		>
			<SlInput
				ref={searchInputRef}
				className='product__grouped-picker-search'
				placeholder={searchPlaceholder}
				onSlInput={onSearchInput}
				value={searchTerm}
				size='small'
				clearable
			>
				<SlIcon name='x-circle' slot='clear-icon' />
			</SlInput>
			<div className='product__grouped-picker-body'>
				{isSearching
					? renderSearchResults()
					: view === 'groups'
						? renderGroupsGrid()
						: renderGroupItems()}
			</div>
			{mode === 'multi' && (
				<div className='product__grouped-picker-footer' slot='footer'>
					{selectedItems.length > 0 && (
						<div className='product__grouped-picker-footer-chips'>
							{selectedItems.map((item) => (
								<span key={item.id} className='product__picker-chip'>
									{item.name}
									<SlIcon
										className='product__picker-chip-remove'
										name='x-circle'
										onClick={() => onRemoveChip(item.id)}
									/>
								</span>
							))}
						</div>
					)}
					<div className='product__grouped-picker-footer-actions'>
						<span>{t('{n} selected').replace('{n}', countText)}</span>
						<SlButton
							className='product__grouped-picker-confirm'
							variant='primary'
							size='small'
							onClick={onClickConfirm}
						>
							{t('Confirm')}
						</SlButton>
					</div>
				</div>
			)}
		</SlDialog>
	);
};

const normalize = (s: string) =>
	s
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase();

export { GroupedPickerDialog };
export type { GroupedPickerItem };
